import { type FC, useState } from 'react';
import type { Order, OrderItem } from '../../types';
import { useToast } from '../../hooks/useToast';
import { useOrderDetail } from '../../hooks/useOrderDetail';
import EditItemModal from './EditItemModal';
import DeleteOrderItemModal from './DeleteOrderItemModal';

interface OrderDetailsTabProps {
    currentOrder: Order;
    items: OrderItem[];
    onRefresh: () => Promise<void>;
}

const OrderDetailsTab: FC<OrderDetailsTabProps> = ({ currentOrder, items: initialItems, onRefresh }) => {
    const { success, error } = useToast();
    const { deleteItem, editItem } = useOrderDetail();

    const [items,              setItems]              = useState<OrderItem[]>(initialItems);
    const [isModalOpen,        setIsModalOpen]        = useState(false);
    const [isDeleteModalOpen,  setIsDeleteModalOpen]  = useState(false);
    const [selectedItem,       setSelectedItem]       = useState<OrderItem | null>(null);
    const [deleteReason,       setDeleteReason]       = useState('');
    const [isSubmitting,       setIsSubmitting]       = useState(false);

    const fmt = (amount: number | string) => `R ${(Number(amount) / 100).toFixed(2)}`;

    const status       = currentOrder.status?.toUpperCase();
    const isOrderLocked = ['DELIVERY_COMPLETE', 'ORDER_CANCELLED', 'ON_ROUTE'].includes(status);

    const handleEditClick   = (item: OrderItem) => { setSelectedItem(item); setIsModalOpen(true); };
    const handleDeleteClick = (item: OrderItem) => { setSelectedItem(item); setDeleteReason(''); setIsDeleteModalOpen(true); };

    const handleDeleteFromEdit = (reason: string) => {
        setIsModalOpen(false);
        setDeleteReason(reason);
        setIsDeleteModalOpen(true);
    };

    const handleModalSave = async (previousQty: number, currentQty: number, reason: string) => {
        if (!selectedItem) return;
        setIsSubmitting(true);
        try {
            await editItem(String(currentOrder.id), selectedItem.id, previousQty, currentQty, reason);
            setItems(prev => prev.map(i =>
                i.id === selectedItem.id
                    ? { ...i, quantity: currentQty, total_price: Number(i.unit_price) * currentQty }
                    : i
            ));
            success(`${selectedItem.product_name} updated — audit trail logged`);
            setIsModalOpen(false);
            setSelectedItem(null);
            await onRefresh();
        } catch (err) {
            error(err instanceof Error ? err.message : 'Failed to update item');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDeleteConfirm = async (confirmReason: string) => {
        if (!selectedItem) return;
        setIsSubmitting(true);
        try {
            const finalReason = deleteReason || confirmReason;
            await deleteItem(String(currentOrder.id), selectedItem.id, finalReason);
            setItems(prev => prev.filter(i => i.id !== selectedItem.id));
            success(`${selectedItem.product_name} deleted — audit trail logged`);
            setIsDeleteModalOpen(false);
            setSelectedItem(null);
            setDeleteReason('');
            await onRefresh();
        } catch (err) {
            error(err instanceof Error ? err.message : 'Failed to delete item');
        } finally {
            setIsSubmitting(false);
        }
    };

    if ((!items || items.length === 0) && !currentOrder.notes?.trim()) {
        return (
            <div className="rounded-2xl bg-white border border-stone-200 shadow-sm p-12 text-center">
                <p className="text-sm text-stone-500">No products added to this order yet.</p>
            </div>
        );
    }

    return (
        <>
            {currentOrder.notes?.trim() && (
                <div className="mb-4 rounded-2xl bg-white border border-stone-200 shadow-sm overflow-hidden">
                    <div className="bg-stone-50 border-b border-stone-200 px-6 py-3">
                        <h3 className="text-xs font-semibold text-stone-900 uppercase tracking-wide">Order Details / Notes</h3>
                    </div>
                    <div className="px-6 py-4">
                        <p className="whitespace-pre-wrap text-sm text-stone-900">{currentOrder.notes}</p>
                    </div>
                </div>
            )}

            {items.length > 0 && (
                <div className="rounded-2xl bg-white border border-stone-200 shadow-sm overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full divide-y divide-stone-200">
                            <thead className="bg-stone-50">
                            <tr>
                                {['Product', 'SKU', 'Unit Price', 'Qty', 'Total', 'Actions'].map(h => (
                                    <th key={h} className={`px-6 py-3 text-xs font-semibold text-stone-900 uppercase tracking-wide ${h === 'Unit Price' || h === 'Qty' || h === 'Total' ? 'text-right' : h === 'Actions' ? 'text-center print:hidden' : 'text-left'}`}>{h}</th>
                                ))}
                            </tr>
                            </thead>
                            <tbody className="divide-y divide-stone-200">
                            {items.map(item => (
                                <tr key={item.id} className="hover:bg-stone-50 transition">
                                    <td className="px-6 py-4 text-sm text-stone-900">{item.product_name}</td>
                                    <td className="px-6 py-4 text-sm text-stone-900">{item.sku}</td>
                                    <td className="px-6 py-4 text-right text-sm text-stone-900">{fmt(item.unit_price)}</td>
                                    <td className="px-6 py-4 text-right">
                                        <span className="text-sm font-medium text-stone-900">{item.quantity}</span>
                                    </td>
                                    <td className="px-6 py-4 text-right text-sm font-semibold text-stone-900">{fmt(item.total_price)}</td>
                                    <td className="px-6 py-4 text-center print:hidden">
                                        <div className="flex justify-center gap-2">
                                            <button onClick={() => handleEditClick(item)} disabled={isSubmitting || isOrderLocked}
                                                    className="inline-flex items-center rounded px-2.5 py-1 text-xs font-medium bg-stone-100 text-stone-900 hover:bg-stone-200 disabled:opacity-50">
                                                Edit
                                            </button>
                                            <button onClick={() => handleDeleteClick(item)} disabled={isSubmitting || isOrderLocked}
                                                    className="inline-flex items-center rounded px-2.5 py-1 text-xs font-medium bg-red-100 text-red-700 hover:bg-red-200 disabled:opacity-50">
                                                Delete
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                            </tbody>
                        </table>
                    </div>
                    <div className="bg-stone-50 px-6 py-4 border-t border-stone-200">
                        <div className="flex justify-end">
                            <div>
                                <p className="text-xs text-stone-500 uppercase tracking-wide">Order Total</p>
                                <p className="mt-1 text-2xl font-bold text-stone-900">
                                    {fmt(items.reduce((sum, i) => sum + Number(i.total_price), 0))}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <EditItemModal
                isOpen={isModalOpen}
                item={selectedItem}
                onClose={() => { setIsModalOpen(false); setSelectedItem(null); }}
                onSave={handleModalSave}
                onDeleteTriggered={handleDeleteFromEdit}
                isSubmitting={isSubmitting}
            />

            <DeleteOrderItemModal
                isOpen={isDeleteModalOpen}
                item={selectedItem}
                initialReason={deleteReason}
                onClose={() => { setIsDeleteModalOpen(false); setSelectedItem(null); setDeleteReason(''); }}
                onConfirm={handleDeleteConfirm}
                isSubmitting={isSubmitting}
            />
        </>
    );
};

export default OrderDetailsTab;